import React from 'react';
import type { SearchUserWabikenMetasQuery, UserWabikenMeta } from '@/API';
import {
  SearchableSortDirection,
  SearchableUserWabikenMetaSortableFields,
} from '@/API';
import type { GraphQLResultEx2 } from '@/shared/hooks/useAmplifyFetcher';
import { useMyLibraryUrlParams } from '../_hooks/useMyLibraryUrlParams';
import { useSearchUserWabikenMetasQuery } from '../_hooks/useSearchUserWabikenMetasQuery';

const ITEMS_PER_PAGE = 20;

type State = {
  pageStatus: 'INIT' | 'PREPARED';
  userList: UserWabikenMeta[];
  totalCount: number;
  totalPage: number;
  currentPageNo: number;
  hasPrev: boolean;
  hasNext: boolean;
};

type Action = {
  type: 'INITIALIZED';
  payload: {
    response: GraphQLResultEx2<SearchUserWabikenMetasQuery>;
    currentPageNo: number;
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'INITIALIZED': {
      const { response, currentPageNo } = action.payload;

      const userList = (
        response.data?.searchUserWabikenMetas?.items ?? []
      ).filter((item): item is UserWabikenMeta => item !== null);

      const totalCount = response.data?.searchUserWabikenMetas?.total ?? 0;

      return {
        ...state,
        pageStatus: 'PREPARED',
        userList,
        totalCount,
        totalPage: Math.ceil(totalCount / ITEMS_PER_PAGE),
        currentPageNo,
        hasPrev: currentPageNo > 1,
        hasNext: currentPageNo < Math.ceil(totalCount / ITEMS_PER_PAGE),
      };
    }
  }
};

type UsePurchasedList = {
  state: State;
};

export function usePurchasedList(): UsePurchasedList {
  const { currentPageNo, searchQueryString, sortOrder } =
    useMyLibraryUrlParams();

  const [state, dispatch] = React.useReducer(reducer, {
    pageStatus: 'INIT',
    userList: [],
    totalCount: 0,
    totalPage: 1,
    currentPageNo: 1,
    hasPrev: false,
    hasNext: false,
  });

  const { searchUserWabikenMetasQuery: searchQuery } =
    useSearchUserWabikenMetasQuery();

  React.useEffect(() => {
    searchQuery({
      sort: [
        {
          direction:
            sortOrder === 'add_desc' || sortOrder === 'name_desc'
              ? SearchableSortDirection.desc
              : SearchableSortDirection.asc,
          field:
            sortOrder === 'add_desc' || sortOrder === 'add_asc'
              ? SearchableUserWabikenMetaSortableFields.activatedAt
              : SearchableUserWabikenMetaSortableFields.contentDisplayNameKana,
        },
      ],
      filter: {
        notValidAfter: {
          // ignore this condition if debug flag is true
          gt: Math.round(new Date().getTime() / 1000),
        },
        or: searchQueryString
          ? [
              {
                contentDisplayName: {
                  match: searchQueryString,
                },
              },
              {
                contentDisplayNameKana: {
                  wildcard: `*${searchQueryString}*`,
                },
              },
            ]
          : undefined,
      },
      from: (currentPageNo - 1) * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
    }).then(({ response }) => {
      dispatch({
        type: 'INITIALIZED',
        payload: {
          response,
          currentPageNo,
        },
      });
    });
  }, [currentPageNo, searchQuery, searchQueryString, sortOrder]);

  return {
    state,
  };
}
